import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import * as query from '../../../query'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateTuple } from './create-tuple'
import { CommonName } from './utils'

// TODO: maybe { '@$': '$position' } references the current position and {
// '@$': '$value' } references the current value?
/**
 * TODO (iterates through each field/element and maps to new value)
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface Map<out T = any> {
  /** {@link Map} */
  readonly [Key.Map]: T extends readonly any[]
    ? readonly [
        Expression<readonly any[]>,
        Expression<
          T extends readonly (infer E)[]
            ? // By default, if T = any => E = unknown, so this explicitly sets T = any => E = any
              T extends never
              ? any
              : E
            : never
        >,
      ]
    : never
}
export namespace Map {
  export const isMap: TypeChecker<Map> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Map>({
      [Key.Map]: TypeChecker.isAny, // TODO:
    }),
  ).usingAlias('Fn.Map')
  export const assertIsMap: TypeAsserter<Map> = TypeAsserter.Factory.from(isMap)

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends readonly unknown[] = readonly unknown[],
  > extends fnNode.Node<
    T,
    Key.Map,
    {
      readonly list: expressionNode.Node<readonly unknown[]>
      readonly transformation: expressionNode.Node
      readonly positionName: query.Variable.Name.Reserved
      readonly valueName: query.Variable.Name.Reserved
    }
  > {
    public readonly type = common.value.types.Intrinsic.List

    constructor(arg: Node<T>['arg']) {
      super(Key.Map, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Map> = {
    createNode: (fn, context) => {
      const [listExpression, transformationExpression] = fn[Key.Map]
      const list = expressionNode.from(listExpression, context)
      if (expressionNode.ForValue.isForValue(list)) {
        TypeAsserter.assertIsReadonlyArray(list.value)
        // First, create a transformation node for each item in the list
        const nodes: expressionNode.Node[] = list.value.map(
          (originalValue, i) =>
            expressionNode.from(transformationExpression, {
              ...context,
              variables: {
                ...context.variables,
                [query.Variable.Name.Reserved.from(CommonName.Position)]: i,
                [query.Variable.Name.Reserved.from(CommonName.Value)]:
                  originalValue,
              },
            }),
        )
        // If every transformation node is a value node, we can return the final
        // result. Otherwise, we will need to do the transformation during the
        // query, but we already have a node for each item in the list, so we
        // can just explicitly create that list (rather than doing the map
        // during the query)
        return nodes.every(expressionNode.ForValue.isForValue)
          ? new expressionNode.ForValue(nodes.map(node => node.value))
          : new CreateTuple.Node(nodes)
      }

      const transformation = expressionNode.from(
        transformationExpression,
        context,
      )
      return new Node({
        list,
        positionName: query.Variable.Name.Reserved.from(CommonName.Position),
        transformation,
        valueName: query.Variable.Name.Reserved.from(CommonName.Value),
      })
    },
    expressionsInArgs: fn => fn[Key.Map],
    typeChecker: isMap,
  }
}
